import {
  api,
  type IIntegrationToken,
  type IIntegrationTokenSetDTO,
  type IUserGenerated,
} from "@/logic/api";
import { useDate } from "@/utility/useDate";
import { SessionContext } from "@/utility/useSession";
import { useContext, useEffect, useState } from "react";
import NotchedCard from "../cards/NotchedCard/NotchedCard";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyIcon from "@mui/icons-material/Key";
import KeyOffIcon from "@mui/icons-material/KeyOff";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CopyIconButton from "../common/CopyIconButton";

import type { SxProps, Theme } from "@mui/material/styles";

const MAX_ACTIVE_TOKENS = 2;

const pressableSx = {
  transition:
    "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
  "&:active": { transform: "scale(0.97)" },
  "&:focus-visible": {
    boxShadow: (theme: Theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
  },
} satisfies SxProps<Theme>;

type TokenRevokeTarget = {
  setUuid: string;
  tokenUuid: string;
  tokenKey: string;
};

type ServiceUserTokenManagementV2Props = {
  generatedUser?: IUserGenerated | null;
  onClose: () => void;
  userUuid: string;
};

const ServiceUserTokenManagementV2 = ({
  generatedUser,
  onClose,
  userUuid,
}: ServiceUserTokenManagementV2Props) => {
  const { current: session } = useContext(SessionContext);
  const { t } = useTranslation();

  const createTokenRequest = api.hooks.integrationToken.createToken();
  const revokeTokenRequest = api.hooks.integrationToken.revokeToken();
  const createSetRequest = api.hooks.integrationToken.createSet();
  const getSetRequest = api.hooks.integrationToken.getSetByCredentialUuid();

  const [newToken, setNewToken] = useState<string>();
  const [tokenSet, setTokenSet] = useState<IIntegrationTokenSetDTO | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<TokenRevokeTarget | null>(
    null,
  );

  const createTokenSet = () => {
    createSetRequest.send(
      session.context,
      {
        credential: userUuid,
        tenant: session.site?.tenant.id ?? "",
        scopes: ["service"],
        title: "title",
      },
      (result) => {
        if (result) {
          setTokenSet(result);
        }
        setIsLoading(false);
      },
    );
  };

  const loadTokenSet = () => {
    getSetRequest.send(session.context, { uuid: userUuid }, (result) => {
      if (result) {
        setTokenSet(result);
        setIsLoading(false);
        return;
      }
      createTokenSet();
    });
  };

  useEffect(() => {
    if (!userUuid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    if (generatedUser && session.site && tokenSet === null) {
      createTokenSet();
      return;
    }

    loadTokenSet();
  }, [userUuid]);

  const generateToken = (integrationTokenSetUuid: string) => {
    setIsGenerating(true);
    createTokenRequest.send(
      session.context,
      { integrationTokenSetUuid, expiresAt: null },
      (result) => {
        if (result) {
          loadTokenSet();
          setNewToken(result.token);
        }
        setIsGenerating(false);
      },
    );
  };

  const revokeToken = (target: TokenRevokeTarget) => {
    setIsRevoking(true);
    revokeTokenRequest.send(
      session.context,
      {
        integrationTokenSetUuid: target.setUuid,
        integrationTokenUuid: target.tokenUuid,
        reason: "User requested",
      },
      (result) => {
        if (result) {
          loadTokenSet();
        }
        setIsRevoking(false);
        setTokenToRevoke(null);
      },
    );
    setNewToken("");
  };

  return (
    <>
      <DialogTitle id="alert-dialog-slide-title" sx={{ textAlign: "center" }}>
        {t("users.tokenManagement")}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {newToken ? <NewTokenAlert token={newToken} /> : null}
        <ActiveTokensPanel
          isLoading={isLoading}
          tokenSet={tokenSet}
          isGenerating={isGenerating}
          onGenerate={generateToken}
          onRevoke={setTokenToRevoke}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
          data-testid="close-token-management"
        >
          {t("general.close")}
        </Button>
      </DialogActions>
      <RevokeTokenDialog
        target={tokenToRevoke}
        isRevoking={isRevoking}
        onCancel={() => setTokenToRevoke(null)}
        onConfirm={revokeToken}
      />
    </>
  );
};

function NewTokenAlert({ token }: { token: string }) {
  const { t } = useTranslation();

  return (
    <Alert
      severity="success"
      icon={<KeyIcon />}
      sx={{ mb: 3, alignItems: "flex-start", textAlign: "left" }}
      data-testid="new-token-alert"
    >
      <Typography fontWeight="bold" sx={{ mb: 1 }}>
        {t("users.newTokenCreated")}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          component="code"
          sx={{
            fontFamily: "monospace",
            bgcolor: "action.hover",
            px: 1,
            py: 0.5,
            borderRadius: 1,
            wordBreak: "break-all",
            fontSize: "0.875rem",
          }}
        >
          {token}
        </Typography>
        <CopyIconButton textToCopy={token} />
      </Box>
    </Alert>
  );
}

function ActiveTokensPanel({
  isLoading,
  tokenSet,
  isGenerating,
  onGenerate,
  onRevoke,
}: {
  isLoading: boolean;
  tokenSet: IIntegrationTokenSetDTO | null;
  isGenerating: boolean;
  onGenerate: (integrationTokenSetUuid: string) => void;
  onRevoke: (target: TokenRevokeTarget) => void;
}) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <NotchedCard
        style={{
          width: "100%",
          maxWidth: "550px",
        }}
        label={t("users.activeTokens")}
      >
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </NotchedCard>
    );
  }

  const now = Date.now();
  const tokens =
    tokenSet?.tokens.filter(
      (token) => !token.expiresAt || now < token.expiresAt.getTime(),
    ) ?? [];
  const isEmpty = tokens.length === 0;
  const isAtLimit = tokens.length >= MAX_ACTIVE_TOKENS;
  const scopesLabel = tokenSet?.scopes.join(", ") ?? "";

  const generateButton = tokenSet ? (
    <Button
      variant={isEmpty ? "contained" : "text"}
      onClick={() => onGenerate(tokenSet.uuid)}
      disabled={isGenerating || isAtLimit}
      startIcon={!isGenerating && !isAtLimit ? <AddIcon /> : undefined}
      data-testid={isEmpty ? "generate-new-token-empty" : "generate-new-token"}
      sx={
        isEmpty
          ? {
              ...pressableSx,
              fontWeight: "bold",
              minWidth: 180,
              minHeight: 36,
              backgroundColor: (theme: Theme) =>
                theme.palette.brand?.main ?? theme.palette.primary.main,
              color: (theme: Theme) => theme.palette.primary.contrastText,
              "&:hover": {
                backgroundColor: (theme: Theme) =>
                  theme.palette.brand?.dark ?? theme.palette.primary.dark,
              },
            }
          : {
              ...pressableSx,
              "&:hover": {
                backgroundColor: (theme: Theme) => theme.palette.action.hover,
              },
              mt: 1,
              minWidth: 180,
              minHeight: 36,
            }
      }
      disableRipple
      disableElevation
    >
      {isGenerating ? (
        <CircularProgress size={20} color="inherit" />
      ) : isAtLimit ? (
        t("users.maxTokensReached")
      ) : (
        t("users.generateNewToken")
      )}
    </Button>
  ) : null;

  return (
    <NotchedCard
      style={{
        width: "100%",
        maxWidth: "550px",
      }}
      label={`${t("users.activeTokens")} (${tokens.length}/${MAX_ACTIVE_TOKENS})`}
    >
      {tokenSet && isEmpty ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 3,
            borderRadius: 1,
            bgcolor: "action.hover",
          }}
          data-testid="empty-tokens-state"
        >
          <KeyOffIcon
            sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
            aria-hidden="true"
          />
          <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
            {t("users.noActiveTokens")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("users.noActiveTokensHint")}
          </Typography>
          {generateButton}
        </Box>
      ) : null}
      {tokenSet && !isEmpty ? (
        <Box>
          {tokens.map((token) => (
            <ActiveTokenRow
              key={token.uuid}
              token={token}
              setUuid={tokenSet.uuid}
              scopesLabel={scopesLabel}
              onRevoke={onRevoke}
            />
          ))}
          {generateButton}
        </Box>
      ) : null}
    </NotchedCard>
  );
}

function ActiveTokenRow({
  token,
  setUuid,
  scopesLabel,
  onRevoke,
}: {
  token: IIntegrationToken;
  setUuid: string;
  scopesLabel: string;
  onRevoke: (target: TokenRevokeTarget) => void;
}) {
  const { t } = useTranslation();
  const { localizeDate } = useDate();
  const createdAt = localizeDate(token.createdAt);
  const expiresAt = token.expiresAt ? localizeDate(token.expiresAt) : null;

  return (
    <Box
      component="article"
      data-testid={`token-item-${token.tokenKey}`}
      sx={{
        mb: 2,
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: "monospace",
            fontWeight: 600,
            mb: 0.5,
            wordBreak: "break-all",
          }}
        >
          {token.tokenKey}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("users.tokenCreatedAt")}{" "}
          <span title={createdAt.toFormat("yyyy-MM-dd HH:mm:ss")}>
            {createdAt.toFormat("MMM d, yyyy")}
          </span>
          {" · "}
          {expiresAt ? (
            <>
              {t("users.tokenExpiresAt")}{" "}
              <span title={expiresAt.toFormat("yyyy-MM-dd HH:mm:ss")}>
                {expiresAt.toFormat("MMM d, yyyy")}
              </span>
            </>
          ) : (
            t("users.tokenNeverExpires")
          )}
          {" · "}
          {scopesLabel}
        </Typography>
      </Box>
      <Tooltip title={t("users.revokeToken")}>
        <IconButton
          onClick={() =>
            onRevoke({
              setUuid,
              tokenUuid: token.uuid,
              tokenKey: token.tokenKey,
            })
          }
          size="small"
          aria-label={t("users.revokeToken")}
          data-testid={`revoke-token-${token.tokenKey}`}
          sx={{
            color: "#6b7280",
            "&:hover": {
              color: "#ef4444",
              bgcolor: "rgba(239, 68, 68, 0.1)",
            },
          }}
        >
          <DeleteIcon sx={{ fontSize: 18 }} aria-hidden="true" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function RevokeTokenDialog({
  target,
  isRevoking,
  onCancel,
  onConfirm,
}: {
  target: TokenRevokeTarget | null;
  isRevoking: boolean;
  onCancel: () => void;
  onConfirm: (target: TokenRevokeTarget) => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={target !== null}
      onClose={onCancel}
      aria-labelledby="revoke-dialog-title"
      aria-describedby="revoke-dialog-description"
    >
      <DialogTitle id="revoke-dialog-title">
        {t("users.revokeTokenConfirmTitle")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="revoke-dialog-description">
          {t("users.revokeTokenConfirmMessage")}
        </DialogContentText>
        {target !== null ? (
          <Typography
            component="code"
            sx={{
              fontFamily: "monospace",
              bgcolor: "action.hover",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              wordBreak: "break-all",
              fontSize: "0.875rem",
              display: "block",
              mt: 2,
            }}
          >
            {target.tokenKey}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onCancel}
          disabled={isRevoking}
          variant="text"
          sx={{
            ...pressableSx,
            "&:hover": {
              backgroundColor: (theme: Theme) => theme.palette.action.hover,
            },
          }}
          disableRipple
          disableElevation
        >
          {t("general.cancel")}
        </Button>
        <Button
          onClick={() => {
            if (target !== null) {
              onConfirm(target);
            }
          }}
          color="error"
          variant="contained"
          disabled={isRevoking}
          data-testid="confirm-revoke-token"
          sx={{
            ...pressableSx,
            fontWeight: "bold",
            minWidth: 120,
            minHeight: 36,
          }}
          disableRipple
          disableElevation
        >
          {isRevoking ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("users.revokeToken")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ServiceUserTokenManagementV2;
